/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { InputAction } from "../../enums/InputAction";
import type { TInputEvent, InputInterceptor } from "../types/TInputEvent";
import type { LongPressConfig } from "../types/TLongPress";

/**
 * Result of `createLongPressInterceptor`.
 *
 * Callers MUST call `dispose()` when the interceptor is no longer needed
 * (e.g., inside the `NavigationEngine.destroy()` flow) to clear any active
 * timers. Failing to do so can invoke `onLongPress` callbacks after the
 * associated controllers have been cleaned up (FR-01 fix).
 */
export interface LongPressInterceptorHandle {
  /** The interceptor function to pass to `InterceptorChain.use()`. */
  interceptor: InputInterceptor;
  /** Clears all pending long-press timers. Must be called on teardown. */
  dispose: () => void;
}

/**
 * Detects long-press gestures on configurable InputActions.
 *
 * Starts a timer on the first `keydown` for a matching action.
 * If the key is released (`keyup`) before the duration elapses,
 * the timer is cancelled (short press). Otherwise the `onLongPress`
 * callback fires.
 *
 * @param configs - One entry per action to watch.
 * @returns A {@link LongPressInterceptorHandle} containing the interceptor
 *   function and a `dispose()` method that clears all pending timers.
 */
export function createLongPressInterceptor(
  configs: LongPressConfig[]
): LongPressInterceptorHandle {
  const timers = new Map<InputAction, ReturnType<typeof setTimeout>>();

  const interceptor: InputInterceptor = (event: TInputEvent): void => {
    for (const config of configs) {
      if (event.action !== config.action) {
        continue;
      }

      if (event.eventType === "keydown" && !event.repeat) {
        const timer = setTimeout((): void => {
          config.onLongPress();
          timers.delete(config.action);
        }, config.duration);
        timers.set(config.action, timer);
      } else if (event.eventType === "keyup") {
        const timer = timers.get(config.action);
        if (timer !== null && timer !== undefined) {
          clearTimeout(timer);
          timers.delete(config.action);
        }
      }
    }
  };

  /**
   * FR-01 fix — clears every pending long-press timer so that callbacks
   * cannot fire after the navigation engine has been destroyed.
   */
  const dispose = (): void => {
    for (const timer of timers.values()) {
      clearTimeout(timer);
    }
    timers.clear();
  };

  return { interceptor, dispose };
}
